import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import Logo from "@/shared/flavours/remindme/assets/icons/logo.svg";
import { rememberNavbarReturnUrl } from "./auth";
import { authenticatedItems, hiddenNavigationRoutes, profileItems, publicItems, type NavbarItem } from "./config";
import NavbarIcon from "./Icons";
import { useNavbarAuth } from "./useNavbarAuth";
import { useNavbarVisibility } from "./visibility";
import "./navbar.scss";

const ProfileIdentity = lazy(() => import("./ProfileIdentity"));

export type NavbarProps = {
  hideMain?: boolean;
  hideHelp?: boolean;
  hideUser?: boolean;
  frontend?: boolean;
  referralHref?: string;
  onHomeNavigate?: () => void;
  onNavigate?: (href: string) => void;
  onPrefetch?: (href: string) => void;
};

const REFERRAL_LABEL = "Freunde werben";

const isExternal = (href: string) => /^https?:\/\//i.test(href);

const shouldUseClientNavigation = (event: React.MouseEvent<HTMLAnchorElement>, href: string) =>
  !isExternal(href) &&
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

const LinkItem = ({
  item,
  mobile = false,
  onNavigate,
  onPrefetch,
}: {
  item: NavbarItem;
  mobile?: boolean;
  onNavigate?: (href: string) => void;
  onPrefetch?: (href: string) => void;
}) => (
  <a
    href={item.href}
    rel={isExternal(item.href) ? "noopener noreferrer" : undefined}
    onMouseEnter={() => onPrefetch?.(item.href)}
    onFocus={() => onPrefetch?.(item.href)}
    onClick={(event) => {
      if (!onNavigate || !shouldUseClientNavigation(event, item.href)) return;
      event.preventDefault();
      onNavigate(item.href);
    }}
  >
    {mobile ? <NavbarIcon name="chevron" size={18} /> : null}
    {item.label}
  </a>
);

const Navbar = ({
  hideMain = false,
  hideHelp = false,
  hideUser = false,
  frontend = false,
  referralHref,
  onHomeNavigate,
  onNavigate,
  onPrefetch,
}: NavbarProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [profileIdentityRequested, setProfileIdentityRequested] = useState(false);
  const [profileEmail, setProfileEmail] = useState<string>();
  const navbarRef = useRef<HTMLElement>(null);
  const mobileTriggerRef = useRef<HTMLButtonElement>(null);
  const mobilePanelRef = useRef<HTMLDivElement>(null);
  const { loggedIn, email, initials } = useNavbarAuth();
  const bodyVisibility = useNavbarVisibility();
  const visibility = {
    hideMain: hideMain || bodyVisibility.hideMain,
    hideHelp: hideHelp || bodyVisibility.hideHelp,
    hideUser: hideUser || bodyVisibility.hideUser,
  };
  const routeHidesNavigation = hiddenNavigationRoutes.some((route) => window.location.pathname.includes(route));
  const mainItems = useMemo(() => (loggedIn ? authenticatedItems : publicItems), [loggedIn]);
  const allHidden = (visibility.hideMain || routeHidesNavigation) && visibility.hideHelp && visibility.hideUser;
  const showReferral = !!referralHref && loggedIn && !visibility.hideUser && !routeHidesNavigation;

  const closeMobileMenu = useCallback((restoreFocus = true) => {
    setMobileOpen(false);
    setOpenMenu(null);
    if (restoreFocus) window.requestAnimationFrame(() => mobileTriggerRef.current?.focus());
  }, []);
  const navigateLink = onNavigate ? (href: string) => onNavigate(href) : undefined;
  const navigateMobileLink = onNavigate
    ? (href: string) => {
        closeMobileMenu(false);
        onNavigate(href);
      }
    : undefined;

  useEffect(() => {
    if (!mobileOpen) return;

    const focusableSelector = 'a[href], button:not([disabled]):not([tabindex="-1"])';
    const firstItem = mobilePanelRef.current?.querySelector<HTMLElement>(focusableSelector);
    const focusFrame = window.requestAnimationFrame(() => firstItem?.focus());

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMobileMenu();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        mobilePanelRef.current?.querySelectorAll<HTMLElement>(focusableSelector) || [],
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeMobileMenu, mobileOpen]);

  useEffect(() => {
    if (!openMenu || mobileOpen) return;
    const closeDropdown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const trigger = navbarRef.current?.querySelector<HTMLButtonElement>('.navbar-dropdown > button[aria-expanded="true"]');
      event.preventDefault();
      setOpenMenu(null);
      trigger?.focus();
    };
    const closeOutside = (event: PointerEvent) => {
      const expandedDropdown = navbarRef.current
        ?.querySelector('.navbar-dropdown > button[aria-expanded="true"]')
        ?.parentElement;
      if (!expandedDropdown?.contains(event.target as Node)) setOpenMenu(null);
    };
    document.addEventListener("keydown", closeDropdown);
    document.addEventListener("pointerdown", closeOutside);
    return () => {
      document.removeEventListener("keydown", closeDropdown);
      document.removeEventListener("pointerdown", closeOutside);
    };
  }, [mobileOpen, openMenu]);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
    setProfileEmail(undefined);
    setProfileIdentityRequested(false);
  }, [loggedIn]);

  const openDropdownFromKeyboard = (event: React.KeyboardEvent<HTMLButtonElement>, label: string) => {
    if (event.key !== "ArrowDown") return;
    event.preventDefault();
    const dropdown = event.currentTarget.parentElement;
    setOpenMenu(label);
    window.requestAnimationFrame(() => dropdown?.querySelector<HTMLElement>(".navbar-submenu a")?.focus());
  };

  const renderReferralLink = (mobile = false) =>
    showReferral && referralHref ? (
      <a
        className={mobile ? "navbar-referral-mobile" : "navbar-referral"}
        href={referralHref}
        onClick={(event) => {
          if (!onNavigate || !shouldUseClientNavigation(event, referralHref)) return;
          event.preventDefault();
          if (mobile) closeMobileMenu(false);
          onNavigate(referralHref);
        }}
      >
        <NavbarIcon name="gift" size={18} />
        {REFERRAL_LABEL}
      </a>
    ) : null;

  const renderMainItems = (mobile = false) =>
    !visibility.hideMain && !routeHidesNavigation
      ? mainItems.map((item) =>
          item.children ? (
            <div
              className="navbar-dropdown"
              key={item.label}
              onMouseEnter={mobile ? undefined : () => setOpenMenu(item.label)}
              onMouseLeave={mobile ? undefined : () => setOpenMenu(null)}
            >
              <button
                type="button"
                aria-haspopup="true"
                aria-expanded={openMenu === item.label}
                onClick={() => setOpenMenu(mobile && openMenu === item.label ? null : item.label)}
                onKeyDown={(event) => openDropdownFromKeyboard(event, item.label)}
              >
                {mobile ? <NavbarIcon name="chevron" size={18} /> : null}
                {item.label}
                {!mobile ? <NavbarIcon name="chevron" size={16} /> : null}
              </button>
              {openMenu === item.label ? (
                <div className="navbar-submenu">
                  {item.children.map((child) => (
                    <LinkItem
                      key={child.label}
                      item={child}
                      onNavigate={mobile ? navigateMobileLink : navigateLink}
                      onPrefetch={onPrefetch}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <LinkItem
              key={item.label}
              item={item}
              mobile={mobile}
              onNavigate={mobile ? navigateMobileLink : navigateLink}
              onPrefetch={onPrefetch}
            />
          ),
        )
      : null;

  return (
    <div className={`rm-navbar-space${frontend ? " rm-navbar-space--frontend" : ""}`}>
      <nav ref={navbarRef} className="rm-navbar" aria-label="main-nav-bar">
        <a
          className="navbar-logo"
          href="/"
          aria-label="remind.me Startseite"
          onMouseEnter={() => onPrefetch?.("/")}
          onFocus={() => onPrefetch?.("/")}
          onClick={(event) => {
            if (!onHomeNavigate || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            onHomeNavigate();
          }}
        >
          <img src={Logo} width="121" height="40" alt="remind.me" />
        </a>

        {/* Burger comes before the desktop block in the DOM so that, on mobile, the first
            <svg> inside nav[aria-label="main-nav-bar"] is the (visible) burger — the E2E
            suite selects it via `nav[aria-label="main-nav-bar"] svg`. */}
        <div className="navbar-mobile-nav">
          {!allHidden ? (
            <button
              ref={mobileTriggerRef}
              className="navbar-mobile-trigger"
              type="button"
              aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"}
              aria-controls="navbar-mobile-menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(true)}
            >
              <NavbarIcon name="menu" size={37} />
            </button>
          ) : null}
        </div>

        <div className="navbar-desktop">
          <div className="navbar-main">{renderMainItems()}</div>
          <div className="navbar-actions">
            {renderReferralLink()}
            {!visibility.hideHelp && !routeHidesNavigation ? (
              <>
                <a className="navbar-icon-link" href="/kontakt" aria-label="Kontakt" onMouseEnter={() => onPrefetch?.("/kontakt")} onFocus={() => onPrefetch?.("/kontakt")} onClick={(event) => {
                  if (!onNavigate || !shouldUseClientNavigation(event, "/kontakt")) return;
                  event.preventDefault();
                  onNavigate("/kontakt");
                }}><NavbarIcon name="contact" /></a>
                <a className="navbar-icon-link" href="/fragen-und-antworten" aria-label="Fragen & Antworten" onMouseEnter={() => onPrefetch?.("/fragen-und-antworten")} onFocus={() => onPrefetch?.("/fragen-und-antworten")} onClick={(event) => {
                  if (!onNavigate || !shouldUseClientNavigation(event, "/fragen-und-antworten")) return;
                  event.preventDefault();
                  onNavigate("/fragen-und-antworten");
                }}><NavbarIcon name="help" /></a>
              </>
            ) : null}
            {!visibility.hideUser ? loggedIn ? (
              <div
                className="navbar-dropdown navbar-profile"
                onMouseEnter={() => {
                  setProfileIdentityRequested(true);
                  setOpenMenu("profile");
                }}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <button
                  type="button"
                  aria-label="Profil"
                  aria-haspopup="true"
                  aria-expanded={openMenu === "profile"}
                  onPointerEnter={() => setProfileIdentityRequested(true)}
                  onFocus={() => setProfileIdentityRequested(true)}
                  onKeyDown={(event) => openDropdownFromKeyboard(event, "profile")}
                  onClick={() => {
                    setProfileIdentityRequested(true);
                    setOpenMenu("profile");
                  }}
                >
                  <span className="navbar-avatar">
                    {profileIdentityRequested ? (
                      <Suspense fallback={initials || <NavbarIcon name="profile" />}>
                        <ProfileIdentity onEmail={setProfileEmail} fallback={initials} />
                      </Suspense>
                    ) : initials || <NavbarIcon name="profile" />}
                  </span>
                </button>
                {openMenu === "profile" ? (
                  <div className="navbar-submenu navbar-profile-menu">
                    {profileEmail || email ? <span className="navbar-email">{profileEmail || email}</span> : null}
                    {profileItems.map((item) => <LinkItem key={item.label} item={item} onNavigate={onNavigate} onPrefetch={onPrefetch} />)}
                    <a className="navbar-logout" href="/logout"><NavbarIcon name="logout" size={18} />Ausloggen</a>
                  </div>
                ) : null}
              </div>
            ) : (
              <a className="navbar-login" href="/login" onMouseEnter={() => onPrefetch?.("/login")} onFocus={() => onPrefetch?.("/login")} onClick={(event) => {
                rememberNavbarReturnUrl();
                if (!onNavigate || !shouldUseClientNavigation(event, "/login")) return;
                event.preventDefault();
                onNavigate("/login");
              }}>Einloggen</a>
            ) : null}
          </div>
        </div>

        {mobileOpen && typeof document !== "undefined"
          ? createPortal(
              // Portalled to <body> so it escapes the header's stacking context.
              <div className="navbar-mobile-layer" role="dialog" aria-modal="true" aria-label="Navigation">
                <button className="navbar-backdrop" type="button" tabIndex={-1} aria-hidden="true" onClick={() => closeMobileMenu()} />
                <div ref={mobilePanelRef} id="navbar-mobile-menu" className="navbar-mobile">
                  {!visibility.hideUser && !loggedIn ? <a className="navbar-mobile-login" href="/login" onClick={(event) => {
                    rememberNavbarReturnUrl();
                    if (!onNavigate || !shouldUseClientNavigation(event, "/login")) return;
                    event.preventDefault();
                    closeMobileMenu(false);
                    onNavigate("/login");
                  }}>Einloggen</a> : null}
                  {!visibility.hideUser && loggedIn && email ? <span className="navbar-email">{email}</span> : null}
                  <div className="navbar-mobile-links navbar-mobile-main">
                    {renderMainItems(true)}
                    {renderReferralLink(true)}
                  </div>
                  {!visibility.hideHelp && !routeHidesNavigation ? (
                    <div className="navbar-mobile-links navbar-mobile-help">
                      <a href="/kontakt" onClick={(event) => {
                        if (!onNavigate || !shouldUseClientNavigation(event, "/kontakt")) return;
                        event.preventDefault();
                        closeMobileMenu(false);
                        onNavigate("/kontakt");
                      }}><NavbarIcon name="contact" size={18} />Kontakt</a>
                      <a href="/fragen-und-antworten" onClick={(event) => {
                        if (!onNavigate || !shouldUseClientNavigation(event, "/fragen-und-antworten")) return;
                        event.preventDefault();
                        closeMobileMenu(false);
                        onNavigate("/fragen-und-antworten");
                      }}><NavbarIcon name="help" size={18} />Fragen &amp; Antworten</a>
                    </div>
                  ) : null}
                  {!visibility.hideUser && loggedIn ? (
                    <div className="navbar-mobile-links navbar-mobile-user">
                      {profileItems
                        .filter((item) => visibility.hideHelp || routeHidesNavigation || item.href !== "/fragen-und-antworten")
                        .map((item) => <LinkItem key={item.label} item={item} mobile onNavigate={navigateMobileLink} onPrefetch={onPrefetch} />)}
                      <a href="/logout"><NavbarIcon name="logout" size={18} />Ausloggen</a>
                    </div>
                  ) : null}
                </div>
              </div>,
              document.body,
            )
          : null}
      </nav>
    </div>
  );
};

export default Navbar;
